import { Dashboard } from "@/models/dashboard/Dashboard";

/**
 * Whose dashboard this is, as the user should read it (LAR-661). An administrator edited a
 * personal dashboard named like the organisation's and took it for the real one (LAR-280), so
 * the header pill, the dropdown rows and the edit banner all render from this one value and can
 * never disagree.
 */
export type DashboardIdentityKind =
  | "personal"
  | "organisation-published"
  | "organisation-draft"
  | "parent-readonly";

export interface DashboardIdentity {
  kind: DashboardIdentityKind;
  /** A published organisation dashboard edited since; colleagues still see the published version. */
  hasUnpublishedChanges: boolean;
  /** The parent organisation's name for an inherited dashboard, null otherwise or when unknown. */
  parentTenantName: string | null;
}

const kindOf = (dashboard: Dashboard): DashboardIdentityKind => {
  // An inherited dashboard is always published and released by its owner (LAR-93); to the
  // sub-tenant it is simply somebody else's, so this wins over every other flag.
  if (dashboard.isFromParentTenant) return "parent-readonly";
  if (!dashboard.isOrganizationDashboard) return "personal";
  return dashboard.isPublished
    ? "organisation-published"
    : "organisation-draft";
};

export const getDashboardIdentity = (
  dashboard: Dashboard
): DashboardIdentity => {
  const kind = kindOf(dashboard);
  return {
    kind,
    hasUnpublishedChanges:
      kind === "organisation-published" && dashboard.hasUnpublishedChanges,
    parentTenantName:
      kind === "parent-readonly" ? dashboard.parentTenantName : null,
  };
};

/** Used when an inherited dashboard arrives without its owner's name. */
const PARENT_TENANT_FALLBACK_NAME = "hovedorganisationen";

export const parentTenantDisplayName = (
  parentTenantName: string | null
): string => parentTenantName ?? PARENT_TENANT_FALLBACK_NAME;

/** One of the current tenant's own organisation dashboards, published or still a draft. */
export const isOwnOrganisationDashboard = (dashboard: Dashboard): boolean => {
  const { kind } = getDashboardIdentity(dashboard);
  return kind === "organisation-published" || kind === "organisation-draft";
};

/**
 * `long` stands next to the dashboard name; `short` sits on a dropdown row, where the group
 * heading already says whose the dashboard is.
 */
export const getDashboardIdentityLabel = (
  identity: DashboardIdentity
): { long: string; short: string } => {
  switch (identity.kind) {
    case "personal":
      return { long: "Personligt", short: "Personligt" };
    case "organisation-published":
      return { long: "Organisation · Publiceret", short: "Publiceret" };
    case "organisation-draft":
      return { long: "Organisation · Kladde", short: "Kladde" };
    case "parent-readonly":
      return {
        long: `Fra ${parentTenantDisplayName(identity.parentTenantName)} · Skrivebeskyttet`,
        short: "Skrivebeskyttet",
      };
  }
};

export interface DashboardsByAudience {
  parent: Dashboard[];
  organisation: Dashboard[];
  personal: Dashboard[];
  /** Inherited dashboards all come from the one parent organisation. */
  parentTenantName: string | null;
}

/** The dropdown's groups, each in the order the API returned its dashboards. */
export const groupDashboardsByAudience = (
  dashboards: Dashboard[]
): DashboardsByAudience => {
  const groups: DashboardsByAudience = {
    parent: [],
    organisation: [],
    personal: [],
    parentTenantName: null,
  };
  for (const dashboard of dashboards) {
    const { kind } = getDashboardIdentity(dashboard);
    if (kind === "parent-readonly") groups.parent.push(dashboard);
    else if (kind === "personal") groups.personal.push(dashboard);
    else groups.organisation.push(dashboard);
  }
  groups.parentTenantName = groups.parent[0]?.parentTenantName ?? null;
  return groups;
};
